import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Newsletter signup card. Drop into article footers, sidebars, or below-fold.
 *
 * Props:
 *   source:   string identifying where the signup came from (analytics)
 *   variant:  'inline' (default) | 'sticky-bottom'
 *   isSpanish: boolean — switch to Spanish copy
 */
export const NewsletterCta = ({ source = 'blog_cta', variant = 'inline', isSpanish = false }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await apiFetch('/api/newsletter/stats');
      if (data?.active_subscribers > 0) setStats(data.active_subscribers);
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !/.+@.+\..+/.test(email)) {
      setError(isSpanish ? 'Por favor ingresa un correo válido' : 'Please enter a valid email');
      return;
    }
    setSubmitting(true);
    const { data, error: apiError } = await apiFetch('/api/newsletter/subscribe', {
      method: 'POST',
      body: { email: email.trim().toLowerCase(), source, language: isSpanish ? 'es' : 'en' },
    });
    setSubmitting(false);
    if (apiError) {
      setError(apiError);
      return;
    }
    setDone(true);
    if (data?.already_subscribed) {
      // still show success, but note it
    }
  };

  const heading = isSpanish ? 'Una frase nueva cada semana' : 'One new phrase per week';
  const subheading = isSpanish
    ? 'Recursos claros y prácticos para cerrar brechas de comunicación. Sin spam, cancela cuando quieras.'
    : 'Clear, practical tools for bridging communication gaps. No spam, unsubscribe anytime.';

  if (done) {
    return (
      <Card className={`border-[#00E5CC]/40 bg-[#00E5CC]/5 ${variant === 'sticky-bottom' ? 'rounded-none border-x-0 border-b-0' : ''}`} data-testid="newsletter-cta-success">
        <CardContent className="p-5 text-center">
          <CheckCircle2 className="h-10 w-10 mx-auto text-[#00E5CC] mb-2" />
          <h3 className="font-semibold mb-1">
            {isSpanish ? '¡Listo! Revisa tu correo.' : "You're in! Check your inbox."}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isSpanish
              ? 'Te enviamos un mensaje de bienvenida. Tu primera frase llega esta semana.'
              : "We just sent a welcome email. Your first phrase arrives this week."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`border-[#00E5CC]/40 bg-gradient-to-br from-[#00E5CC]/8 to-transparent ${variant === 'sticky-bottom' ? 'rounded-none border-x-0 border-b-0' : ''}`}
      data-testid="newsletter-cta"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-[#00E5CC]/15 flex items-center justify-center flex-shrink-0">
            <Mail className="h-5 w-5 text-[#00E5CC]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base sm:text-lg leading-tight">{heading}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">{subheading}</p>
            {stats !== null && stats >= 25 && (
              <p className="text-[11px] text-[#00E5CC] mt-1.5 font-medium" data-testid="newsletter-cta-stats">
                {isSpanish ? `Únete a ${stats.toLocaleString()} suscriptores` : `Join ${stats.toLocaleString()} subscribers`}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-cta-form">
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={isSpanish ? 'tu@correo.com' : 'you@email.com'}
            disabled={submitting}
            className="flex-1"
            data-testid="newsletter-cta-input"
            aria-label={isSpanish ? 'Correo electrónico' : 'Email address'}
          />
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold sm:w-auto"
            data-testid="newsletter-cta-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isSpanish ? 'Suscribirse' : 'Subscribe')}
          </Button>
        </form>
        {error && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400" data-testid="newsletter-cta-error">{error}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsletterCta;
